import React, { useEffect, useMemo, useState } from "react";
import { Boxes, RefreshCcw, Warehouse } from "lucide-react";
import { warehouseService } from "../../../services/warehouse.service";
import { inventoryService } from "../../../services/inventory.service";
import {
    ActionButton,
    Card,
    SelectLike,
    SummaryCard,
    Pagination,
} from "./components/inventory-ui";
import InventoryTab from "./components/InventoryTab";

function normalizeList(res) {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.items)) return res.items;
    if (Array.isArray(res?.data?.items)) return res.data.items;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.rows)) return res.rows;
    return [];
}

function formatNumber(value) {
    const num = Number(value || 0);
    if (!Number.isFinite(num)) return "0";
    return new Intl.NumberFormat("en-IN").format(num);
}

export default function AdminStockPage() {
    const [warehouses, setWarehouses] = useState([]);
    const [warehouseFilter, setWarehouseFilter] = useState("__none__");

    const [stockItems, setStockItems] = useState([]);
    const [stockLoading, setStockLoading] = useState(false);
    const [selectedStockProductId, setSelectedStockProductId] = useState("");
    const [batches, setBatches] = useState([]);
    const [batchesLoading, setBatchesLoading] = useState(false);

    const [stockPage, setStockPage] = useState(1);
    const [toast, setToast] = useState("");

    const STOCK_PAGE_SIZE = 8;

    const loadMasters = async () => {
        try {
            const res = await warehouseService.listWarehouses({ page: 1, limit: 200 });
            setWarehouses(normalizeList(res));
        } catch (e) {
            setToast(e?.message || "Failed to load warehouses");
        }
    };

    const loadStock = async () => {
        if (warehouseFilter === "__none__") {
            setStockItems([]);
            setSelectedStockProductId("");
            setBatches([]);
            return;
        }

        try {
            setStockLoading(true);

            const params = { page: 1, limit: 100 };
            if (warehouseFilter) params.warehouseId = warehouseFilter;

            const res = await inventoryService.listStockSummary(params);
            const items = normalizeList(res);

            setStockItems(items);

            if (!items.length) {
                setSelectedStockProductId("");
                setBatches([]);
                return;
            }

            setSelectedStockProductId((prev) =>
                prev && items.some((x) => x.productId === prev)
                    ? prev
                    : items[0]?.productId || ""
            );
        } catch (e) {
            setToast(e?.message || "Failed to load stock summary");
            setStockItems([]);
        } finally {
            setStockLoading(false);
        }
    };

    const loadBatches = async () => {
        if (!warehouseFilter || warehouseFilter === "__none__" || !selectedStockProductId) {
            setBatches([]);
            return;
        }

        try {
            setBatchesLoading(true);
            const res = await inventoryService.listBatches({
                warehouseId: warehouseFilter,
                productId: selectedStockProductId,
            });
            setBatches(normalizeList(res));
        } catch (e) {
            setToast(e?.message || "Failed to load batches");
        } finally {
            setBatchesLoading(false);
        }
    };

    useEffect(() => {
        loadMasters();
    }, []);

    useEffect(() => {
        setStockPage(1);
        loadStock();
    }, [warehouseFilter]);

    useEffect(() => {
        loadBatches();
    }, [warehouseFilter, selectedStockProductId]);

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(""), 3000);
        return () => clearTimeout(t);
    }, [toast]);

    const totalQty = useMemo(
        () => stockItems.reduce((sum, item) => sum + Number(item.qtyAvailable || 0), 0),
        [stockItems]
    );

    const totalPages = Math.max(1, Math.ceil(stockItems.length / STOCK_PAGE_SIZE));

    const paginatedStockItems = useMemo(() => {
        const start = (stockPage - 1) * STOCK_PAGE_SIZE;
        return stockItems.slice(start, start + STOCK_PAGE_SIZE);
    }, [stockItems, stockPage]);

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                <div>
                    <h1 className="text-[30px] font-bold text-[#d86a78]">Stock</h1>
                    <p className="mt-1 text-[13px] font-medium  text-slate-500">
                        Select warehouse filter to view assigned product stock.
                    </p>
                </div>

                <ActionButton variant="soft" onClick={loadStock}>
                    <RefreshCcw size={16} /> Refresh
                </ActionButton>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <SummaryCard
                    icon={Warehouse}
                    title="Selected Filter"
                    value={
                        warehouseFilter === "__none__"
                            ? "Select Warehouse First"
                            : warehouseFilter === ""
                                ? "All"
                                : "Warehouse"
                    }
                    subtitle="Stock filter"
                />
                <SummaryCard
                    icon={Boxes}
                    title="Products"
                    value={stockItems.length}
                    subtitle="Visible stock products"
                />
                <SummaryCard
                    icon={Boxes}
                    title="Available Qty"
                    value={formatNumber(totalQty)}
                    subtitle="Available stock quantity"
                />
            </div>

            <Card className="p-4 sm:p-5">
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-[280px_1fr]">
                    <SelectLike
                        value={warehouseFilter}
                        onChange={setWarehouseFilter}
                        placeholder="Select Warehouse First"
                        options={[
                            { value: "", label: "All warehouses" },
                            ...warehouses.map((w) => ({
                                value: w.id,
                                label: w.name || w.code || w.id,
                            })),
                        ]}
                    />
                </div>
            </Card>

            {warehouseFilter === "__none__" ? (
                <Card className="p-8 text-center">
                    <div className="text-[15px] font-bold text-slate-700">
                        Please select a warehouse
                    </div>
                    <div className="mt-1 text-[13px] font-medium text-slate-500">
                        Choose “All Warehouses” or a specific warehouse to view stock.
                    </div>
                </Card>
            ) : (
                <InventoryTab
                    stockLoading={stockLoading}
                    visibleStockItems={paginatedStockItems}
                    selectedStockProductId={selectedStockProductId}
                    setSelectedStockProductId={setSelectedStockProductId}
                    stockItems={stockItems}
                    warehouseFilter={warehouseFilter}
                    batchesLoading={batchesLoading}
                    batches={batches}
                    pagination={
                        <Pagination
                            currentPage={stockPage}
                            totalPages={totalPages}
                            onPageChange={setStockPage}
                        />
                    }
                />
            )}

            {toast ? (
                <div className="fixed bottom-5 right-5 z-[999] rounded-[14px] bg-slate-900 px-4 py-3 text-[13px] font-semibold text-white shadow-xl">
                    {toast}
                </div>
            ) : null}
        </div>
    );
}
